import { supabase } from '@/integrations/supabase/client';
import { ServiceError, withServiceErrorHandling } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';

export interface UserStats {
  postsCreated: number;
  campaignsCreated: number;
}

export class StatsService {
  private static logger = logger.child({ component: 'StatsService' });

  /**
   * Get statistics for a user
   */
  static async getUserStats(userId: string): Promise<UserStats> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching user stats', { userId });

      const [
        { count: postsCreated },
        { count: campaignsCreated },
      ] = await Promise.all([
        supabase
          .from('posts')
          .select('*', { count: 'exact', head: true })
          .eq('created_by', userId),
        supabase
          .from('campaigns')
          .select('*', { count: 'exact', head: true })
          .eq('created_by', userId),
      ]);

      const stats = {
        postsCreated: postsCreated || 0,
        campaignsCreated: campaignsCreated || 0,
      };

      this.logger.info('User stats fetched', { userId, ...stats });

      return stats;
    }, { operation: 'getUserStats' });
  }

  /**
   * Get organization statistics
   */
  static async getOrganizationStats(organizationId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching organization stats', { organizationId });

      const [
        { count: totalPosts },
        { count: publishedPosts },
        { count: scheduledPosts },
        { count: totalCampaigns },
        { count: activeCampaigns },
        { count: teamMembers },
        { count: connectedAccounts },
      ] = await Promise.all([
        supabase
          .from('posts')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', organizationId),
        supabase
          .from('posts')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', organizationId)
          .eq('status', 'published'),
        supabase
          .from('posts')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', organizationId)
          .eq('status', 'scheduled'),
        supabase
          .from('campaigns')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', organizationId),
        supabase
          .from('campaigns')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', organizationId)
          .eq('status', 'active'),
        supabase
          .from('user_roles')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', organizationId),
        supabase
          .from('social_accounts')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', organizationId)
          .eq('is_active', true),
      ]);

      const stats = {
        totalPosts: totalPosts || 0,
        publishedPosts: publishedPosts || 0,
        scheduledPosts: scheduledPosts || 0,
        totalCampaigns: totalCampaigns || 0,
        activeCampaigns: activeCampaigns || 0,
        teamMembers: teamMembers || 0,
        connectedAccounts: connectedAccounts || 0,
      };

      this.logger.info('Organization stats fetched', { organizationId, ...stats });

      return stats;
    }, { operation: 'getOrganizationStats' });
  }
}
